import { useCallback } from 'react'
import { PrimaryButton, SecondaryButton, Typography } from '@musinsa/layer-components-desktop'
import { Modal } from '@musinsa/mwms-ds'
import styled, { createGlobalStyle } from 'styled-components'

export type ResultVariant = 'partialFailure' | 'excelFailure' | 'directFailure'

interface Props {
  variant: ResultVariant
  failedRows?: string
  message?: string
  onClose: () => void
  onReupload?: () => void
}

function SingleBatchSettingResultDialog({ variant, failedRows, message, onClose, onReupload }: Props) {
  const handleCopy = useCallback(() => {
    if (failedRows) navigator.clipboard.writeText(failedRows.split(', ').join('\n'))
  }, [failedRows])

  const showReupload = variant === 'partialFailure' || variant === 'excelFailure'

  const title = showReupload ? '파일 업로드 실패' : '등록 실패'

  const fallbackMessage = (
    <>
      일시적인 오류로 처리에 실패했어요.
      <br />
      문제가 지속될 경우 개발팀에 문의해주세요.
    </>
  )

  return (
    <Modal open customWidth={400} hideFooter contentClassName="result-dialog-content" onClose={onClose}>
      <ResultDialogGlobalStyle />
      <DialogBody>
        <ContentRow>
          <IconWrapper>
            <AlertTriangleIcon />
          </IconWrapper>
          <TextContent>
            <Typography name="base-title-s-semibold" colorToken="fg">
              {title}
            </Typography>
            <Typography name="base-text-l" colorToken="fg">
              {message ?? fallbackMessage}
            </Typography>
          </TextContent>
        </ContentRow>

        {variant === 'partialFailure' && failedRows && (
          <FailureBanner>
            <BannerContent>
              <BannerRow>
                <Typography name="base-text-m" colorToken="fg">
                  실패 행
                </Typography>
              </BannerRow>
              <BannerRow>
                <Typography name="base-text-m" colorToken="fg">
                  {failedRows}
                </Typography>
              </BannerRow>
              <SecondaryButton variant="outlined" size="xsmall" label="복사" onClick={handleCopy} />
            </BannerContent>
          </FailureBanner>
        )}
      </DialogBody>

      <DialogFooter $hasBorder={variant === 'partialFailure' && !!failedRows}>
        {showReupload ? (
          <>
            <SecondaryButton variant="outlined" size="large" widthMode="compact" label="취소" onClick={onClose} />
            <PrimaryButton variant="filled" size="large" widthMode="compact" label="재업로드" onClick={onReupload} />
          </>
        ) : (
          <SecondaryButton variant="outlined" size="medium" widthMode="compact" label="확인" onClick={onClose} />
        )}
      </DialogFooter>
    </Modal>
  )
}

function AlertTriangleIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" fill="#e02020" />
      <line x1="12" y1="9" x2="12" y2="13" stroke="white" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1" fill="white" />
    </svg>
  )
}

const ResultDialogGlobalStyle = createGlobalStyle`
  .result-dialog-content {
    padding: 0 !important;
  }
`

const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 24px 0;
`

const ContentRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const FailureBanner = styled.div`
  background: var(--banner-info-bg, rgba(224, 224, 224, 0.2));
  border: 1px solid var(--banner-info-border, #ebebeb);
  border-radius: 4px;
  padding: 16px 12px;
`

const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const BannerRow = styled.div`
  display: flex;
  gap: 8px;
`

const DialogFooter = styled.div<{ $hasBorder?: boolean }>`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 24px;
  ${({ $hasBorder }) => $hasBorder && `border-top: 1px solid var(--stroke-divider-low, rgba(138, 138, 138, 0.1));`}
`

export default SingleBatchSettingResultDialog
